import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import Strings from '../../../Strings';
import { fileActions } from '../../../actions/files';
import { supportedLanguages } from '../../../utils/language';
import { isSuperUser } from '../../../utils/userRoles';
import { openModalAction } from '../../../actions/modal';
import { ADD_FILE_MODAL_NAME } from '../../../modals/AddFileModal';
import { ASSIGN_DEVICE_INSTRUCTION_VIDEO_MODAL_NAME } from '../../../modals/AssignDeviceInstructionVideoModal';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { HEADER_BUTTON_BLUE } from '../../../constants';
import infoIcon from '../../../images/rx/infoIcon.svg';
import { DEVICE_INSTRUCTION_VIDEO_PURPOSE, deviceInstructionActions } from './deviceInstructionActions';
import { selectorKey, selectorLabel } from './deviceInstructionHelpers';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition } from '../../../components/AdvancedFilters/FiltersDefinition';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';

const isSuccess = response => response?.type?.endsWith('/result');
const errorMessage = (response, fallback) => response?.response?.data?.error?.message || fallback;
const rowTestId = selector => {
  const target = selector.show_assignment_scope && selector.target_organization_id
    ? `organization:${selector.target_organization_id}:`
    : '';
  return selector.selector_kind === 'model'
    ? `device-instruction[${target}model:${selector.device_model_id}:${selector.language}]`
    : `device-instruction[${target}${selector.device_type}:${selector.language}]`;
};

const sourceLabel = source => {
  if (!source) return Strings.deviceInstructionNotConfigured;
  return source.type === 'global'
    ? Strings.deviceInstructionGlobal
    : `${Strings.deviceInstructionOrgLevels?.[source.type] || source.type}: ${source.name}`;
};

const videoKey = item => `${item.target_organization_id || 'global'}-${item.language}-${selectorKey(item)}`;
const languageLabel = language => Strings.languagesEnum?.[language?.toLowerCase()] || language;
const assignmentTargetLabel = assignment => `${assignment.selector_kind === 'model'
  ? Strings.deviceInstructionModel
  : Strings.deviceInstructionDeviceType}: ${selectorLabel(assignment)}`;
const formatSize = size => {
  if (!Number.isFinite(size)) return '—';
  if (size < 1024) return `${size} B`;
  const units = ['KiB', 'MiB', 'GiB'];
  let value = size / 1024;
  let unit = units[0];
  for (let index = 1; value >= 1024 && index < units.length; index += 1) {
    value /= 1024;
    unit = units[index];
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${unit}`;
};
const formatCreatedAt = createdAt => createdAt ? new Date(createdAt).toLocaleString() : '—';

function MissingStorageWarning() {
  return (
    <div className="icon-and-text-container" role="alert">
      <div
        className="icon-container alert"
        aria-label={Strings.deviceInstructionMissingStorageLabel}
        data-tooltip-id="tooltip"
        data-tooltip-content={Strings.deviceInstructionMissingStorageHelp}
      />
      {Strings.deviceInstructionMissingStorage}
    </div>
  );
}

function StorageUsage({ file }) {
  const label = file.usage_count === 1
    ? Strings.deviceInstructionUsedOnce
    : Strings.formatString(Strings.deviceInstructionUsedCount, file.usage_count);
  if (file.usage_count === 0) {
    return <span>{label}</span>;
  }
  return (
    <details data-testid={`storage-usage[${file.object_name}]`}>
      <summary>{label}</summary>
      <ul>
        {(file.assignments || []).map(assignment => (
          <li key={`${assignment.org_id || 'global'}-${assignment.language}-${selectorKey(assignment)}`}>
            {`${sourceLabel(assignment)} · ${languageLabel(assignment.language)} · ${assignmentTargetLabel(assignment)}`}
          </li>
        ))}
      </ul>
    </details>
  );
}

StorageUsage.propTypes = {
  file: PropTypes.object.isRequired,
};

function StorageObjectCell({ row, getValue }) {
  return (
    <div data-testid={`storage-file[${row.original.object_name}]`}>
      <div>{getValue()}</div>
      {row.original.preview_url && (
        <video
          controls
          width="480"
          src={row.original.preview_url}
          aria-label={Strings.formatString(Strings.deviceInstructionStoredPreview, row.original.object_name)}
        />
      )}
    </div>
  );
}

StorageObjectCell.propTypes = {
  getValue: PropTypes.func.isRequired,
  row: PropTypes.object.isRequired,
};

function StorageStatusCell({ row }) {
  const file = row.original;
  return (
    <>
      {!file.in_storage && <MissingStorageWarning />}
      {!file.in_database && (
        <div className="icon-and-text-container">
          <div
            className="icon-container warning"
            aria-label={Strings.deviceInstructionUnregisteredLabel}
            data-tooltip-id="tooltip"
            data-tooltip-content={Strings.deviceInstructionUnregisteredHelp}
          />
          {Strings.deviceInstructionUnregistered}
        </div>
      )}
      {file.in_database && file.in_storage && <div>{Strings.deviceInstructionAvailable}</div>}
      <StorageUsage file={file} />
    </>
  );
}

StorageStatusCell.propTypes = {
  row: PropTypes.object.isRequired,
};

function VideoDeviceCell({ row, getValue }) {
  return <div data-testid={rowTestId(row.original)}>{getValue()}</div>;
}

VideoDeviceCell.propTypes = {
  getValue: PropTypes.func.isRequired,
  row: PropTypes.object.isRequired,
};

function VideoFileCell({ row, getValue }) {
  const item = row.original;
  return (
    <>
      <div>{getValue()}</div>
      {item.missing_from_storage && <MissingStorageWarning />}
      {item.preview_url && (
        <video
          controls
          width="480"
          src={item.preview_url}
          aria-label={Strings.formatString(Strings.deviceInstructionCurrentPreview, selectorLabel(item))}
        />
      )}
    </>
  );
}

VideoFileCell.propTypes = {
  getValue: PropTypes.func.isRequired,
  row: PropTypes.object.isRequired,
};

function VideoSourceCell({ row }) {
  const item = row.original;
  return (
    <>
      {sourceLabel(item.source)}
      {item.source?.language?.toLowerCase() !== item.language?.toLowerCase() && item.source?.language && (
        <span>{` · ${Strings.formatString(Strings.deviceInstructionVideoLanguage, languageLabel(item.source.language))}`}</span>
      )}
      {!item.show_assignment_scope && (
        <strong>{` · ${item.own_object_name ? Strings.deviceInstructionOwnSetting : Strings.deviceInstructionInherited}`}</strong>
      )}
      {item.selector_kind === 'model' && item.source?.selector_kind === 'type' && (
        <p>{Strings.formatString(Strings.deviceInstructionModelFallsBackToType, item.source.device_type)}</p>
      )}
    </>
  );
}

VideoSourceCell.propTypes = {
  row: PropTypes.object.isRequired,
};

function StorageFiles({ files, preview, onPreview, onAssign, onDelete }) {
  const tableFiles = files.map(file => ({
    ...file,
    preview_url: preview?.objectName === file.object_name ? preview.url : null,
  }));
  const columns = React.useMemo(() => [
    columnHelper.accessor('object_name', {
      header: Strings.deviceInstructionObjectName,
      cell: StorageObjectCell,
      size: 360,
    }),
    columnHelper.accessor('mime_type', {
      header: Strings.deviceInstructionFileType,
      cell: info => info.getValue() || '—',
      enableSorting: false,
      size: 150,
    }),
    columnHelper.accessor('size_bytes', {
      header: Strings.deviceInstructionFileSize,
      cell: info => formatSize(info.getValue()),
      enableSorting: false,
      size: 120,
    }),
    columnHelper.accessor('created_at', {
      header: Strings.deviceInstructionCreated,
      cell: info => formatCreatedAt(info.getValue()),
      sortingFn: 'dateSortingFn',
      size: 200,
    }),
    columnHelper.accessor('usage_count', {
      header: Strings.deviceInstructionStorageStatus,
      cell: StorageStatusCell,
      enableSorting: false,
      size: 500,
    }),
  ], []);

  const buttons = React.useMemo(() => [
    {
      icon: 'preview',
      text: (_, file) => preview?.objectName === file.object_name
        ? Strings.deviceInstructionHidePreview
        : Strings.deviceInstructionPreview,
      visible: (_, file) => file.can_assign,
      highlighted: (_, file) => preview?.objectName === file.object_name,
      onClick: (_, file) => onPreview(file),
      testId: (_, file) => `preview-storage-file[${file.object_name}]`,
    },
    {
      icon: 'plus',
      text: Strings.deviceInstructionAssignStored,
      visible: (_, file) => file.can_assign,
      onClick: (_, file) => onAssign(file),
      testId: (_, file) => `assign-storage-file[${file.object_name}]`,
    },
    {
      icon: 'delete',
      text: Strings.delete,
      visible: (_, file) => file.can_delete,
      onClick: (_, file) => onDelete(file),
      testId: (_, file) => `delete-storage-file[${file.object_name}]`,
    },
  ], [onAssign, onDelete, onPreview, preview]);

  return (
    <section data-testid="device-instruction-storage">
      <PageHeader left={Strings.deviceInstructionStorageFiles} />
      {files.length === 0 && <p>{Strings.deviceInstructionNoStorageFiles}</p>}
      {files.length > 0 && (
        <Table3
          data={tableFiles}
          columns={columns}
          buttons={buttons}
          name="DeviceInstructionStorageFiles"
          sorting={[{ id: 'created_at', desc: true }]}
          enableColumnFiltering={false}
        />
      )}
    </section>
  );
}

StorageFiles.propTypes = {
  files: PropTypes.array.isRequired,
  preview: PropTypes.object,
  onPreview: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function DeviceInstructionVideos({
  organizationId,
  getConfiguration,
  setConfiguration,
  getPreview,
  getStorageInventory,
  getStoragePreview,
  openAddModal,
  openAssignModal,
  openConfirmModal,
  filters,
}) {
  const [language, setLanguage] = React.useState('');
  const [configuration, setConfigurationState] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [savingType, setSavingType] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [inventory, setInventory] = React.useState(null);
  const [inventoryLoading, setInventoryLoading] = React.useState(true);
  const [inventoryError, setInventoryError] = React.useState('');
  const [storagePreview, setStoragePreview] = React.useState(null);
  const missingStorageObjects = React.useMemo(() => new Set(
    (inventory?.data || []).filter(file => !file.in_storage).map(file => file.object_name),
  ), [inventory]);
  const assignmentRows = React.useMemo(() => (inventory?.data || []).flatMap(file =>
    (file.assignments || []).map(assignment => ({
      ...assignment,
      language: assignment.language.toLowerCase(),
      object_name: file.object_name,
      own_object_name: file.object_name,
      source: assignment,
      target_organization_id: assignment.org_id,
      show_assignment_scope: true,
    }))), [inventory]);
  const loadSequence = React.useRef(0);
  const inventoryLoadSequence = React.useRef(0);

  const reload = React.useCallback(async () => {
    loadSequence.current += 1;
    const sequence = loadSequence.current;
    setLoading(true);
    setError('');
    const responses = await Promise.all(supportedLanguages.map(item => getConfiguration(organizationId, item.id)));
    if (sequence !== loadSequence.current) return;
    const failed = responses.find(response => !isSuccess(response) || !Array.isArray(response.response?.data));
    if (!failed) {
      const rows = responses.flatMap((response, index) => response.response.data
        .filter(item => item.object_name)
        .map(item => ({
          ...item,
          language: supportedLanguages[index].id,
          target_organization_id: organizationId || null,
        })));
      setConfigurationState({ ...responses[0].response, data: rows });
    } else {
      setConfigurationState(null);
      setError(errorMessage(failed, Strings.deviceInstructionLoadError));
    }
    setLoading(false);
  }, [getConfiguration, organizationId]);

  const reloadInventory = React.useCallback(async () => {
    inventoryLoadSequence.current += 1;
    const sequence = inventoryLoadSequence.current;
    setInventoryLoading(true);
    setInventoryError('');
    const response = await getStorageInventory();
    if (sequence !== inventoryLoadSequence.current) return;
    if (isSuccess(response) && Array.isArray(response.response?.data)) {
      setInventory(response.response);
    } else {
      setInventory(null);
      setInventoryError(errorMessage(response, Strings.deviceInstructionStorageLoadError));
    }
    setInventoryLoading(false);
  }, [getStorageInventory]);

  React.useEffect(() => {
    setPreview(null);
    setStoragePreview(null);
    reload();
    reloadInventory();
    return () => {
      loadSequence.current += 1;
      inventoryLoadSequence.current += 1;
    };
  }, [reload, reloadInventory]);

  const previewVideo = async selector => {
    setError('');
    if (preview?.key === videoKey(selector)) {
      setPreview(null);
      return;
    }
    setPreview(null);
    const response = await getPreview(selector.target_organization_id, selector.language, selector);
    if (isSuccess(response) && response.response?.playback_url) {
      setPreview({ key: videoKey(selector), url: response.response.playback_url });
    } else {
      setError(errorMessage(response, Strings.deviceInstructionPreviewError));
    }
  };

  const assignStoredVideo = async (
    selector, objectName, reportError, targetOrganizationId = organizationId, videoLanguage = selector.language,
  ) => {
    setSavingType(videoKey({
      ...selector,
      language: videoLanguage,
      target_organization_id: targetOrganizationId || null,
    }));
    setError('');
    try {
      const response = await setConfiguration(
        targetOrganizationId,
        videoLanguage,
        selector,
        objectName,
        DEVICE_INSTRUCTION_VIDEO_PURPOSE,
      );
      if (!isSuccess(response)) {
        reportError(errorMessage(response, Strings.deviceInstructionStoredAssignError));
        return false;
      }
      setPreview(null);
      setStoragePreview(null);
      await Promise.all([reload(), reloadInventory()]);
      if (language && language !== videoLanguage) setLanguage(videoLanguage);
      return true;
    } catch (assignError) {
      reportError(Strings.deviceInstructionStoredAssignError);
      return false;
    } finally {
      setSavingType(null);
    }
  };

  const openAddFile = () => openAddModal({ onUploaded: reloadInventory });

  const openAssignVideo = (storedFile, target) => openAssignModal({
    organizationId: target ? target.target_organization_id : organizationId,
    language,
    existing: configuration?.data || [],
    initialObjectName: storedFile?.object_name,
    target,
    onAssign: (selector, objectName, videoLanguage, targetOrganizationId, reportError) =>
      assignStoredVideo(selector, objectName, reportError, targetOrganizationId, videoLanguage),
  });

  const previewStoredVideo = async file => {
    setInventoryError('');
    if (storagePreview?.objectName === file.object_name) {
      setStoragePreview(null);
      return;
    }
    setStoragePreview(null);
    const response = await getStoragePreview(file.object_name);
    if (isSuccess(response) && response.response?.url) {
      setStoragePreview({ objectName: file.object_name, url: response.response.url });
    } else {
      setInventoryError(errorMessage(response, Strings.deviceInstructionStoredPreviewError));
    }
  };

  const deleteStoredVideo = file => openConfirmModal({
    caption: Strings.deviceInstructionDeleteStored,
    title: Strings.formatString(Strings.deviceInstructionDeleteStoredConfirm, file.object_name),
    onConfirmAction: fileActions.deleteStoredFile(DEVICE_INSTRUCTION_VIDEO_PURPOSE, file.object_name),
    confirmPostAction: () => {
      setStoragePreview(null);
      reloadInventory();
    },
    yesButtonText: Strings.delete,
  });

  const removeAssignment = selector => openConfirmModal({
    caption: Strings.deviceInstructionRemoveAssignment,
    title: Strings.formatString(Strings.deviceInstructionRemoveAssignmentConfirm, selectorLabel(selector), languageLabel(selector.language)),
    onConfirmAction: deviceInstructionActions.resetConfiguration(selector.target_organization_id, selector.language, selector),
    confirmPostAction: () => {
      setPreview(null);
      Promise.all([reload(), reloadInventory()]);
    },
    hideCaution: true,
    yesButtonText: Strings.remove,
  });

  const refresh = () => {
    setPreview(null);
    setStoragePreview(null);
    return Promise.all([reload(), reloadInventory()]);
  };

  const videos = organizationId ? configuration?.data || [] : assignmentRows;
  const appliedFilters = getRelevantFilters(filters);
  const filteredByLanguage = appliedFilters.find(f => f. group === "language");
  const displayedVideos = videos
    .filter(item => !filteredByLanguage || appliedFilters.find(f => f. group === "language" && f.value === item.language))
    .map(item => ({
      ...item,
      device_label: selectorLabel(item),
      language_label: languageLabel(item.language),
      source_sort: `${item.source?.type === 'global' ? '0' : '1'}:${item.source?.name || ''}`,
      missing_from_storage: missingStorageObjects.has(item.object_name),
      preview_url: preview?.key === videoKey(item) ? preview.url : null,
    }));
  const videosReady = !loading && configuration && (organizationId || (!inventoryLoading && inventory));

  const videoColumns = React.useMemo(() => [
    columnHelper.accessor('device_label', {
      header: Strings.deviceInstructionSelector,
      cell: VideoDeviceCell,
      size: 300,
    }),
    columnHelper.accessor('language_label', {
      header: Strings.language,
      enableSorting: false,
      size: 150,
    }),
    columnHelper.accessor('object_name', {
      header: Strings.deviceInstructionAssignedFile,
      cell: VideoFileCell,
      size: 360,
    }),
    columnHelper.accessor('source_sort', {
      header: Strings.deviceInstructionSource,
      cell: VideoSourceCell,
      size: 400,
    }),
  ], []);

  const videoButtons = React.useMemo(() => [
    {
      icon: 'preview',
      text: (_, item) => preview?.key === videoKey(item)
        ? Strings.deviceInstructionHidePreview
        : Strings.deviceInstructionPreview,
      visible: (_, item) => Boolean(item.object_name),
      disabled: (_, item) => savingType === videoKey(item),
      highlighted: (_, item) => preview?.key === videoKey(item),
      onClick: (_, item) => previewVideo(item),
      testId: (_, item) => `preview-${rowTestId(item)}`,
    },
    {
      icon: 'edit',
      text: Strings.deviceInstructionReplace,
      visible: (_, item) => Boolean(item.own_object_name),
      disabled: (_, item) => savingType === videoKey(item),
      onClick: (_, item) => openAssignVideo(null, item),
      testId: (_, item) => `replace-${rowTestId(item)}`,
    },
    {
      icon: 'delete',
      text: Strings.deviceInstructionRemoveAssignment,
      visible: (_, item) => Boolean(item.own_object_name),
      disabled: (_, item) => savingType === videoKey(item),
      onClick: (_, item) => removeAssignment(item),
      testId: (_, item) => `remove-${rowTestId(item)}`,
    },
  ], [preview, savingType]);

  if (!isSuperUser()) return null;

  return (
    <div data-testid="device-instruction-videos">
      <PageHeader
        right={(
          <>
            <AdvancedFilters>
              <Filter definition={getFiltersDefinition().language} />
            </AdvancedFilters>
            <Button
              class="refresh"
              onClick={refresh}
              disabled={loading || inventoryLoading || Boolean(savingType)}
              ariaLabel={Strings.refresh}
              tooltip={Strings.refresh}
            />
            <Button
              class={HEADER_BUTTON_BLUE}
              onClick={openAddFile}
              disabled={Boolean(savingType)}
              title={Strings.deviceInstructionAddFile}
            />
          </>
        )}
        left={(
          <>
            {error && (
              <p role="alert">
                {error}
                <button type="button" onClick={reload}>{Strings.deviceInstructionTryAgain}</button>
              </p>
            )}
            {inventoryError && (
              <p role="alert">
                {inventoryError}
                <button type="button" onClick={reloadInventory}>{Strings.deviceInstructionTryAgain}</button>
              </p>
            )}
            {videosReady && (
              <>
                {Strings.deviceInstructionVideoSection}
                {' '}
                <img
                  src={infoIcon}
                  alt={Strings.deviceInstructionHelpLabel}
                  aria-label={Strings.deviceInstructionHelpLabel}
                  data-testid="device-instruction-list-help"
                  data-tooltip-id="tooltip"
                  data-tooltip-content={Strings.deviceInstructionHelp}
                  data-tooltip-place="bottom"
                />
              </>
            )}
          </>
        )}
      />
      <AdvancedFiltersBar />
      {loading && <p>{Strings.loading}</p>}
      {videosReady && displayedVideos.length === 0 && (
        <p>{organizationId ? Strings.deviceInstructionNoTypes : Strings.deviceInstructionNoAssignments}</p>
      )}
      {videosReady && displayedVideos.length > 0 && (
        <Table3
          data={displayedVideos}
          columns={videoColumns}
          buttons={videoButtons}
          name="DeviceInstructionVideos"
          sorting={[{ id: 'source_sort', desc: false }]}
          enableColumnFiltering={false}
        />
      )}
      {inventoryLoading && <p>{Strings.deviceInstructionLoadingStorage}</p>}
      {!inventoryLoading && inventory && (
        <StorageFiles
          files={inventory.data}
          preview={storagePreview}
          onPreview={previewStoredVideo}
          onAssign={openAssignVideo}
          onDelete={deleteStoredVideo}
        />
      )}
    </div>
  );
}

DeviceInstructionVideos.propTypes = {
  organizationId: PropTypes.string,
  getConfiguration: PropTypes.func.isRequired,
  setConfiguration: PropTypes.func.isRequired,
  getPreview: PropTypes.func.isRequired,
  getStorageInventory: PropTypes.func.isRequired,
  getStoragePreview: PropTypes.func.isRequired,
  openAddModal: PropTypes.func.isRequired,
  openAssignModal: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = dispatch => ({
  getConfiguration: (orgId, language) => dispatch(deviceInstructionActions.getConfiguration(orgId, language)),
  setConfiguration: (orgId, language, selector, objectName, purpose) =>
    dispatch(deviceInstructionActions.setConfiguration(orgId, language, selector, objectName, purpose)),
  getPreview: (orgId, language, selector) => dispatch(deviceInstructionActions.preview(orgId, language, selector)),
  getStorageInventory: () => dispatch(fileActions.getStorageFiles(DEVICE_INSTRUCTION_VIDEO_PURPOSE)),
  getStoragePreview: objectName => dispatch(fileActions.previewStoredFile(DEVICE_INSTRUCTION_VIDEO_PURPOSE, objectName)),
  openAddModal: data => dispatch(openModalAction(ADD_FILE_MODAL_NAME, data)),
  openAssignModal: data => dispatch(openModalAction(ASSIGN_DEVICE_INSTRUCTION_VIDEO_MODAL_NAME, data)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DeviceInstructionVideos);
